/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useState } from 'react'
import { observer } from 'mobx-react'
import { Schema$SportIssue } from '@interface/sport-issue'
import { PopularSearchTermPopupWrap } from '@components/PopularSearchTerm/index.style'
import { ListView } from 'antd-mobile'
import { Button } from '@styles/button.style'
import { MATCH_INSIGHT_TYPE, SPORT_ISSUE_TYPE } from '@v2/constants/sport-issue'
import { YouTubeEmbed } from 'react-social-media-embed'
// img
import popularSearchSlideImg from '@assets/images/bg/sport-issue-popular-search-slide.jpg'
import PopupUtils from '@utils/popup-utils'
import { UploadImgStyle } from '@views/Popup/Chat/index.style'
import useRootStore from '@store/useRootStore'
import _ from 'lodash'
import { useOnMount } from '@utils/reactExt'
import { SportIssueCardWrap, SportIssuePopupListWrap } from '@views/Matchup/MatchUpDetail/index.style'
import AdvImg from '@components/AdvImg'
import { isMobile } from 'react-device-detect'
import { SwiperWrap, TxtLimit2 } from '@styles/base.style'
import { Swiper, SwiperSlide } from 'swiper/react/swiper-react'
import { FreeMode, Pagination } from 'swiper'
import Login from '@views/Login'
import { AdvButton } from '@components/Button'
import { AnalysisProfile } from '@views/Matchup/MatchUpDetail/TabContent/SportTalkContents/AnalysisProfile'
import { PICK_WRITER_TYPE } from '@v2/constants/common'

export interface SportIssuePopupProps {
  matchId: number
}

const SportIssuePopup: React.FC<SportIssuePopupProps> = observer((props => {
  const { matchId } = props
  const { componentStore, authStore, userPickStore, companyPickStore, globalStore } = useRootStore()
  const [ isLoading, setIsLoading ] = useState(false)
  const [ hasMore, setHasMore ] = useState(false)
  const [ sportIssueList, setSportIssueList ] = useState([])
  const isDashboardEnabled = globalStore.getSportTalkDashboardEnabled()

  const fetch = async (reset = false) => {
    try {
      setIsLoading(true)
      const result = await componentStore.fetchSportIssueList(matchId, _.size(sportIssueList), 20)
      if (reset) {
        setSportIssueList(result?.rows || [])
      } else {
        setSportIssueList(_.concat(sportIssueList, result?.rows || []))
      }
      setHasMore(result?.hasMore || false)
    } catch (err) {
      console.error(err)
    }
    setIsLoading(false)
  }

  useOnMount(() => {
    fetch().catch(console.error)
  })

  const endReachedHandler = () => {
    if (!isLoading && hasMore) {
      fetch().catch(console.error)
    }
  }

  const dataSource = new ListView.DataSource({
    rowHasChanged: (row1: any, row2: any) => row1 !== row2,
  })

  const renderHeader = (): React.ReactElement => {
    if (isDashboardEnabled) {
      return <p className="popup-title">스포츠 이슈</p>
    }
    return <Fragment/>
  }

  const renderRow = (item: Schema$SportIssue) => {
    let contents: JSX.Element
    let onClickSportIssueDetail: (() => void) | null = null
    if (item.sportIssueType === SPORT_ISSUE_TYPE.POPULAR_SEARCH_TERM_NEWS) {
      contents = <AdvImg src={popularSearchSlideImg} srcMode={'small'} alt="now 이슈키워드" className="thumbnail"/>
      onClickSportIssueDetail = () => {
        PopupUtils.showPopularSearchTermPopup(item.popularSearchTerm)
      }
    } else {
      const { matchInsightType, attachment } = item.matchInsight
      if (matchInsightType === MATCH_INSIGHT_TYPE.IMAGE) {
        contents = <AdvImg src={attachment} srcMode={'small'} alt={item.content} className="thumbnail"/>
        onClickSportIssueDetail = () => {
          globalStore.pushDialogOk({
            custom: (
              <div css={UploadImgStyle} className={isMobile ? 'is-txt' : 'is-txt pc'}>
                <img src={attachment} alt={item.content}/>
                <p className="txt" css={TxtLimit2}>{item.content}</p>
              </div>
            ),
            className: 'image-full-popup',
            singlePress: '닫기',
            // 뒷배경 클릭 시 닫히도록
            callbackCloseDialogBg: () => {
              globalStore.closeDialog()
            },
          })
        }
      } else if (matchInsightType === MATCH_INSIGHT_TYPE.URL) {
        contents = <div className="embed-wrap thumbnail"><YouTubeEmbed url={attachment} width={'252px'} height={'140px'}/></div>
        onClickSportIssueDetail = () => {
          globalStore.pushDialogOk({
            custom: (
              <div css={UploadImgStyle} className={isMobile ? 'is-txt' : 'is-txt pc'}>
                <YouTubeEmbed url={attachment} width={'100%'}/>
                <p className="txt" css={TxtLimit2}>{item.content}</p>
              </div>
            ),
            className: 'image-full-popup',
            singlePress: '닫기',
            // 뒷배경 클릭 시 닫히도록
            callbackCloseDialogBg: () => {
              globalStore.closeDialog()
            },
          })
        }
      }
    }

    return (
      <SportIssuePopupListWrap>
        <Button
          TP
          onClick={onClickSportIssueDetail}
          className="popular-search-news-btn"
        >
          {contents}
          <p className="title">{item.content}</p>
        </Button>
      </SportIssuePopupListWrap>
    )
  }

  const makeAnalysisSlidesSlides = () => {
    const items = componentStore.sportTalkPickUserInfos
    return _.map(items, (item, index) => {
      const { targetId, userPromiseId } = item

      const onClickAnalysisBtn = async () => {
        if (!authStore.isSignedIn) {
          authStore.showLoginPopup(<Login/>)
        } else {
          if (item.pickWriterType === PICK_WRITER_TYPE.USER_PICK) { // s 멤버십 경기분석 팝업창
            try {
              await userPickStore.fetchMatch(matchId)
              PopupUtils.showUserPickList({
                targetUserPickId: targetId,
              })
            } catch (error) {
              console.error(error)
            }
          } else { //프리미엄 분석가 경기분석 팝업창
            try {
              await companyPickStore.fetchCompanyPick(targetId)
              await companyPickStore.fetchCompanyPickManagers({
                userPromiseIds: [userPromiseId],
              })
              globalStore.setVisibleCompanyPickDetailPopup(true)
            } catch (error) {
              console.error(error)
            }
          }
        }
      }

      return (
        <SwiperSlide className="swipe-item analysis-profile" key={index}>
          <AdvButton
            width={'100%'}
            contents={<AnalysisProfile userInfo={item} lightMode/>}
            onClick={onClickAnalysisBtn}
          />
        </SwiperSlide>
      )
    })
  }

  let sportIssueListContents: JSX.Element
  const analysisSlides = makeAnalysisSlidesSlides()
  if (isMobile) {
    sportIssueListContents = (
      <SwiperWrap className="quick-swipe">
        {analysisSlides}
      </SwiperWrap>
    )
  } else {
    sportIssueListContents = (
      <SwiperWrap className="quick-swipe right-navigation">
        <Swiper
          slidesPerView={'auto'}
          pagination={!isMobile && _.size(analysisSlides) > 0 && { type: 'fraction', clickable: false }}
          navigation={!isMobile}
          spaceBetween={14}
          loop={false}
          shortSwipes
          modules={[FreeMode, Pagination]}
        >
          {analysisSlides}
        </Swiper>
      </SwiperWrap>
    )
  }

  const renderFooter = () => {
    const isDashboardEnabled = globalStore.getSportTalkDashboardEnabled()
    if (isDashboardEnabled) {
      return (
        <Fragment>
          <p className="popup-title">추천 분석글</p>
          <SportIssueCardWrap className={!isMobile && 'pc'}>
            {sportIssueListContents}
          </SportIssueCardWrap>
        </Fragment>
      )
    }
    return <Fragment/>
  }

  return (
    <PopularSearchTermPopupWrap>
      <ListView
        dataSource={dataSource.cloneWithRows(sportIssueList || [])}
        renderHeader={renderHeader}
        renderRow={renderRow}
        renderFooter={renderFooter}
        pageSize={20}
        initialListSize={20}
        onEndReached={endReachedHandler}
        css={{ height: '100%' }}
      />
    </PopularSearchTermPopupWrap>
  )

}))
export default SportIssuePopup
